import { Head } from "@inertiajs/react";
import CtaBand from "@/components/marketing/cta-band";
import PageHero from "@/components/marketing/page-hero";
import SectionHeading from "@/components/marketing/section-heading";
import SmartLink from "@/components/marketing/smart-link";
import SolutionDuo from "@/components/marketing/solution-duo";
import TeamGrid from "@/components/marketing/team-grid";
import PublicLayout from "@/layouts/public-layout";
import homeHeroBuilding from "@/images/marketing/home-hero-building.webp";
import {
  operatingPrinciples,
  siteLinks,
  solutionSummaries
} from "@/data/marketing-site";

const foundationCards = [
  {
    icon: "bi bi-compass",
    title: "Strategisk ro",
    copy: "Det handler ikke kun om at undgå bøder. Det handler også om at reducere personligt ansvar og give ledelsen ro til at møde op i bestyrelseslokalet — og under tilsyn — med fuld dokumentation for, at organisationen lever op til sine forpligtelser. Det er en aktiv beslutning om at ruste organisationen til fremtiden, beskytte sine medarbejdere og sine kunder."
  },
  {
    icon: "bi bi-shield-check",
    title: "Dokumenteret ansvar",
    copy: "Det handler ikke om at have underskrevet en politik. Det handler om at kunne bevise over for myndigheder, kunder og bestyrelse, at ansvaret faktisk er løftet, og at beslutninger er truffet, processer er fulgt og kontroller er udført. Dokumenteret ansvar er det, der adskiller virksomheder, der er compliant, fra virksomheder, der tror, de er det."
  },
  {
    icon: "bi bi-arrow-repeat",
    title: "Compliance i drift",
    copy: "Det handler ikke kun om at nå en slutdato. Det handler om at gøre compliance til en operationel del af hverdagen. Processer der følges. Kontroller der valideres. Dokumentation der opdateres. Compliance i drift er, når organisationens daglige drift også er en fornuftsdrevet, dokumenteret kontrol."
  }
];

function Home() {
  return (
    <>
      <Head title="Obsero | Operationel compliance til NIS2 eller DORA">
        <meta
          head-key="description"
          name="description"
          content="Strategisk ro, dokumenteret ansvar og compliance i drift, hvor Obsero hjælper virksomheder med at omsætte NIS2 eller DORA til operationel handling."
        />
      </Head>

      <PageHero
        narrow
        title={
          <>
            Strategisk ro.
            <br />
            Dokumenteret ansvar.
            <br />
            Compliance i drift.
          </>
        }
        copy="Obsero leverer et operationelt NIS2- eller DORA-compliancefundament, som sikrer dig en hurtigere og nemmere vej mod dokumenteret compliance. Alle politikker, kontroller og processer er gennemtænkt, testet og klar til implementering. Vi har gjort det komplekse arbejde på forhånd. Fundamentet er klar – I skal bare bringe det i drift for at komme sikkert i mål."
        actions={[
          { href: "#loesninger", label: "Se vores løsninger", className: "btn btn-light" },
          { href: siteLinks.demo, label: "Book et møde", className: "btn btn-outline-light" }
        ]}
        imageSrc={homeHeroBuilding}
        imagePosition="center right"
        signals={["NIS2", "DORA", "Governance", "Ledelsesansvar", "Audit-ready"]}
      />

      <section className="public-section public-section--white-surface">
        <div className="public-container">
          <div className="public-split-callout">
            <div className="public-split-callout__lead public-split-callout__lead--accented">
              <SectionHeading
                title="Obsero eksisterer for at hjælpe dig helt i mål."
                copy="Vi hjælper danske virksomheder med at omsætte NIS2 eller DORA til operationel virkelighed — med strategisk ro, dokumenteret ledelsesansvar og en klar vej fra lovkrav til drift. Vi har gjort det svære arbejde for jer. Alt, hvad der skal til, er klar — I skal bare komme i mål."
              />

              <div className="public-inline-links">
                <SmartLink href="#loesninger" className="public-inline-link">
                  Se vores løsninger
                </SmartLink>
              </div>
            </div>

            <div className="public-stack-list">
              {foundationCards.map((step, index) => (
                <article key={step.title} className="public-stack-item">
                  <div className="public-stack-item__index">{String(index + 1).padStart(2, "0")}</div>
                  <div className="public-stack-item__body">
                    <h3 className="public-stack-item__title">{step.title}</h3>
                    <p className="public-stack-item__copy">{step.copy}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="public-section public-section--shell-band">
        <div className="public-container">
          <div className="public-quote-panel public-quote-panel--solo">
            <div className="public-quote-panel__body">
              <h2 className="public-quote-panel__text">
                Cybertrusler venter ikke. Myndighederne heller ikke.
              </h2>
              <p className="public-quote-panel__copy">
                Cyberangreb rammer i dag virksomheder i alle størrelser – og konsekvenserne er massive. De er tekniske, juridiske, økonomiske og personlige. EU har svaret med NIS2 og DORA, to lovgivninger, der stiller præcise og bindende krav til, hvordan virksomheder styrer, dokumenterer og reagerer på cybersikkerhedsrisici. Det er krav, der gør ledelsen personligt ansvarlig. Derfor er det ikke længere nok med planer og hensigtserklæringer. Styringen skal være operationel, dokumenterbar og fungere i praksis.
              </p>

              <div className="public-inline-links public-inline-links--spacious">
                <SmartLink href={siteLinks.nis2} className="public-inline-link">
                  Se NIS2
                </SmartLink>
                <SmartLink href={siteLinks.dora} className="public-inline-link">
                  Se DORA
                </SmartLink>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="public-section public-section--white-surface">
        <div className="public-container">
          <div className="public-statement-grid">
            <div className="public-statement-block">
              <h2 className="public-statement-block__text">
                The Obsero Way er et gennemtestet fundament.
              </h2>
              <p className="public-statement-block__copy">
                Obsero er skabt af specialister med dyb erfaring i IT-sikkerhed og regulatorisk compliance. Vi er med jer hele vejen i vores samarbejde, hvor vi sikrer, at I kommer i mål med et setup, der er tilpasset jeres virksomhed – og at jeres dokumentation virker i praksis.
              </p>
            </div>

            <div className="public-principle-list">
              {operatingPrinciples.map((principle, index) => (
                <article key={principle.title} className="public-principle-item">
                  <div className="public-principle-item__index">{String(index + 1).padStart(2, "0")}</div>
                  <div className="public-principle-item__body">
                    <h3 className="public-principle-item__title">{principle.title}</h3>
                    <p className="public-principle-item__copy">{principle.copy}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="public-section public-section--solution-band" id="loesninger">
        <div className="public-container">
          <SectionHeading
            title="To løsninger. Ét fælles DNA."
          />

          <SolutionDuo
            items={solutionSummaries.map((solution) => ({
              key: solution.key,
              title: solution.label,
              copy: solution.copy,
              fit: solution.key === "core"
                ? ["NIS2- eller DORA-compliant på kun 12 uger.", ...solution.fit]
                : solution.fit,
              href: solution.href
            }))}
          />
        </div>
      </section>

      <section className="public-section public-section--team-tail">
        <div className="public-container">
          <SectionHeading title="Teamet bag Obsero." className="public-team-intro" />
          <TeamGrid />
        </div>
      </section>

      <CtaBand
        title="Er I klar til at tage det næste skridt?"
        copy="Uanset hvor I er i jeres compliance-rejse, finder vi hurtigt ud af, hvad der skal til. Vi demonstrerer løsningen og giver jer et klart billede af vejen frem."
        actions={[
          { href: siteLinks.contact, label: "Kontakt os", className: "btn btn-light" }
        ]}
      />
    </>
  );
}

Home.layout = (page) => <PublicLayout>{page}</PublicLayout>;

export default Home;
